import { Autocomplete, Chip, Grid, Stack, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import DataTable from "../DataGrid/DataGrid";
import {
  APP_BASE_URL,
  ASSET_MAINTENANCE_TABLE_API,
  extractFilters,
  trimAssetName,
  tsToDateFormatter,
  tsToDateTimeFormatter,
} from "../../Common/EndPoints";
import axios from "axios";
import toast from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { PeridioCityTerminologies } from "../Devices/Maintenance/MaintenanceConstants";
import { extractAllowedUserRoleList, extractMasterRoleIds, extractUserDataFromPermission } from "../../Utils/Utils";
import { getLocalStorage, getParsedLocalStorage } from "../../Utils/storage-utils";
import { AutocompleteStationListPop } from "../AutocompleteStationListPop/AutocompleteStationListPop";
import { getDeviceTypes } from "../Devices/Devices";
import "./mis.scss"
import { useSearchParams } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";

const maintenaceCategoryDD = ["SCHEDULED", "ONDEMAND"];

const initialFilters = {
  page: 0,
  pageSize: 10,
  maintenanceType: "",
  deviceTypeIds: [],
  roleId: [],
  division: [],
  section: [],
  station: [],
};

const initialState = {
  data: [],
  totalElements: 0,
};

const maintenanceOverdueColumns: GridColDef[] = [
  {
    field: "gearType",
    headerName: "Asset Type",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "software",
    headerName: "System",
    sortable: true,
    minWidth: 100,
    hideable: true,
    flex: 1,
    valueFormatter: (params) => params.value.code,
    renderCell: (params) => params.value.code,
    // TODO: match with backend system key response 
  },
  {
    field: "gearName",
    headerName: "Asset Name",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "scheduleCode",
    headerName: "Schedule Code",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "distance",
    headerName: "Distance (m)",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "maintenanceCategory",
    headerName: "Maintenance Category",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "periodicity",
    headerName: "Periodicity",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "status",
    headerName: "Schedule Status",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "overdueDays",
    headerName: "Overdue Days",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "expectedDt",
    headerName: "Next Due Date",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    ...tsToDateFormatter,
  },
  {
    field: "completedDt",
    headerName: "Last Done Date",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    ...tsToDateTimeFormatter,
  },
  {
    field: "completedByName",
    headerName: "Last Done By",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    valueGetter(params) {
      return params.value ?? "-";
    },
  },
  {
    field: "designation",
    headerName: "Designation",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
];

function MaintenanceOverdue() {
  const { customer_name_to_id } = useMemo(() => {
    const divisionList = getLocalStorage("permissions", []);
    const {
      outermostKeys: divisions,
      outerToInnerKeys: divisionLists,
      innerToCustomerNames: station,
      customerNameToIdObject: customer_name_to_id,
    }: any = extractUserDataFromPermission(JSON.parse(divisionList));

    return {
      divisions,
      divisionLists,
      station,
      customer_name_to_id,
    };
  }, []);
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get("stn_codes");
  const [filter, setFilter] = useImmer({
    ...initialFilters,
    station: stationId?.split?.(",").map((station: any) => customer_name_to_id[station]) || []
  });
  const [maintenanceOverdueTableLoader, setMaintenanceOverdueTableLoader] = useState(false);
  let roleHr = JSON.parse(localStorage.getItem("roleHierarchies") || "{}");
  const [state, setState] = useImmer(initialState);
  let MASTER_ROLE_IDS = extractMasterRoleIds(roleHr);

  useEffect(() => {
    getMaintenanceOverdueReport();
  }, [filter]);

  const getMaintenanceOverdueReport = async () => {
    try {
      const _filter: any = { ...filter };
      setMaintenanceOverdueTableLoader(true);
      let url = `${APP_BASE_URL}${ASSET_MAINTENANCE_TABLE_API}`;
      let roleIdList = _filter?.roleId?.length > 0 ? _filter.roleId?.toString() : MASTER_ROLE_IDS.toString();

      let params: any = {
        ..._filter,
        page: _filter.page + 1,
        pageSize: _filter.pageSize,
        withResponse: true,
        status: "OVERDUE",
        deviceTypeIds: _filter?.deviceTypeIds?.toString() ?? null,
        zones: _filter?.division?.toString() ?? null,
        divisions: _filter?.section?.toString() ?? null,
        customerIds: _filter?.station?.toString() ?? null,
        // maintenanceType: filter?.maintenanceType?.value ?? null,
        // customerIds,
        // deviceTypeIds: selectedGearType?.typeid ?? null,
        // deviceTypeIds: filters?.deviceTypeIds?.value ?? (allDeviceType === "" ? null : allDeviceType),
        // roleIdList: MASTER_ROLE_IDS.toString(),
        withScheduleAssignment: true,
        roleIdList,
        departmentId: null,
        softwareIds: _filter?.systems?.map((system: any) => system.systemId)?.toString() ?? null,
      };
      delete params["roleId"];
      delete params["systems"];
      // delete params["page"];
      // delete params["pageSize"];
      delete params["division"];
      delete params["section"];
      delete params["station"];

      const res = await axios.get(`${url}?${extractFilters(params)}`);

      if (res?.data?.data?.content) {
        let Response = res.data.data.content.map((row: any, i: number) => {
          let assetName = trimAssetName(row.gearName);
          const raw_description = row.descriptions;
          const parsed_description = raw_description ? JSON.parse(raw_description) : null;
          const distance = parsed_description?.distance ?? null;

          return {
            ...row,
            id: i,
            gearName: assetName,
            periodicity: (PeridioCityTerminologies as any)[row.periodicity] ?? row.periodicity ?? "",
            designation: row.designation ? row.designation : row.scheduleAssignment.userRoleName ?? "-",
            distance: distance ? Math.round(distance) : "NA",
          };
        });
        setState((draft) => {
          draft.data = Response;
          draft.totalElements = res.data.data.totalElements;
        });
        setMaintenanceOverdueTableLoader(false);
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message ?? "Error fetching maintenance overdue report");
      } else {
        toast.error(error?.message ?? "Error fetching maintenance overdue report");
      }
      console.log("MAINTENANCE OVERDUE ERROR", error);
    } finally {
      setMaintenanceOverdueTableLoader(false);
    }
  };

  const filterTableData = (filter: any) => {
    setFilter((oldFilter) => ({
      ...oldFilter,
      ...filter,
      page: 0,
    }));
  };

  return (
    <div className="page-container--card">
      <Typography variant="h6" sx={{ fontWeight: "bold" }} textAlign={"start"}>
        Maintenance Overdue
      </Typography>

      <MaintenanceOverdueFilter filter={filter} onFilter={filterTableData} initialFilters={initialFilters} />

      <DataTable
        tableInfo={{
          id: `maintenance-overdue-table`,
          label: `Maintenance Overdue`,
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={maintenanceOverdueTableLoader}
        rows={state.data}
        columns={maintenanceOverdueColumns}
        rowCount={state.totalElements}
        pageSize={filter.pageSize}
        pageSizeChange={(pageSize: number) =>
          setFilter((draft) => {
            draft.pageSize = pageSize;
          })
        }
        pageIndex={filter.page}
        pageChange={(page: number) =>
          setFilter((draft) => {
            draft.page = page;
          })
        }
        hideExportPDF={true}
        paginationMode="server"
      />
    </div>
  );
}

export default MaintenanceOverdue;

const MaintenanceOverdueFilter = ({
  filter,
  onFilter,
  initialFilters,
}: {
  filter: any;
  onFilter: any;
  initialFilters: any;
}) => {
  const { outerToInnerKeys: divisionLists, innerToCustomerNames: station, outermostKeys: zone, customerNameToIdObject: customer_name_to_id } = usePermissions();

  const [privateFilter, setPrivateFilter] = useImmer(filter);
  const [gearTypeList, setGearTypeList] = useState<any>([]);
  const system_options = useMemo(() => {
    const systems = getParsedLocalStorage("systems_list", []);
    return systems.map((system: any) => ({
      systemId: system.id,
      systemName: system.code,
    }));
  }, []);

  const onChangeFilter = (name: string, value: any | any[]) =>
    setPrivateFilter((draft: any) => {
      draft[name] = value;
    });

  const getRoleIdList = useMemo(() => {
    let roleHierarchies = JSON.parse(localStorage.getItem("roleHierarchies") || "{}");
    let list = extractAllowedUserRoleList(roleHierarchies);
    return list;
  }, []);

  const handleFetchDeviceTypeList = async () => {
    const _device_type_list = await getDeviceTypes();
    setGearTypeList(_device_type_list);
  };

  useEffect(() => {
    handleFetchDeviceTypeList();
  }, []);

  useEffect(() => {
    const filteredStations = filter.station.filter((stn: any) => Object.values(station).flat().map((station: any) => customer_name_to_id[station]).includes(stn));
    onChangeFilter("station", filteredStations);
    onFilter({
      ...filter,
      station: filteredStations,
    });
  }, [station]);

  return (
    <>
      <div className="filters-container">
        <DivisionSectionStationFilter filter={privateFilter} onChangeFilter={onChangeFilter} />

        <Autocomplete
          className="filter-input auto-complete"
          size="small"
          disablePortal
          value={privateFilter.maintenanceType ?? null}
          options={maintenaceCategoryDD}
          onChange={(_, newValue) => {
            onChangeFilter("maintenanceType", newValue || "");
          }}
          renderInput={(params) => (
            <TextField className="filter-input" {...params} label="Maintenance Type" placeholder="Maintenance Type" />
          )}
        />

        <Autocomplete
          size="small"
          className="filter-input auto-complete"
          disableCloseOnSelect
          options={gearTypeList}
          multiple
          renderInput={(params) => (
            <TextField
              className="filter-input"
              {...params}
              label="Select Asset Type"
              placeholder="Select Asset Type"
            />
          )}
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].label} {...getTagProps({ index: 0 })} />
                {value.length > 1 && (
                  <AutocompleteStationListPop title={value.map((val) => val?.label ?? "").toString()}>
                    <Chip size="small" label={`+${value.length - 1}`} />
                  </AutocompleteStationListPop>
                )}
              </div>
            );
          }}
          getOptionLabel={(option: any) => option?.label ?? ""}
          isOptionEqualToValue={(option, value) => option.typeid === value.typeid}
          value={gearTypeList.filter((option: any) => privateFilter?.deviceTypeIds?.includes(option.typeid)) || null}
          onChange={(_, newValue) => {
            onChangeFilter("deviceTypeIds", newValue.map((option: any) => option.typeid) || []);
          }}
        />

        <Autocomplete
          className="filter-input auto-complete"
          size="small"
          multiple
          disableCloseOnSelect
          limitTags={2}
          isOptionEqualToValue={(option, value) => option.systemId === value.systemId}
          getOptionKey={(option) => option.systemId}
          value={privateFilter.systems ?? []}
          options={system_options}
          getOptionLabel={(option) => option.systemName}
          onChange={(_, value) =>
            setPrivateFilter((prev: any) => ({
              ...prev,
              systems: value,
            }))
          }
          renderTags={(value, getTagProps) => {
            return (
              <div>
                <Chip size="small" variant="outlined" label={value[0].systemName} {...getTagProps({ index: 0 })} />
                {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
              </div>
            );
          }}
          renderInput={(params) => <TextField {...params} label="Systems" placeholder="Systems" />}
        />

        <Autocomplete
          className="filter-input auto-complete"
          size="small"
          disablePortal
          disableCloseOnSelect
          value={getRoleIdList.filter((role) => privateFilter.roleId?.includes(role.id)) ?? []}
          options={getRoleIdList}
          onChange={(_, newValue) => {
            onChangeFilter(
              "roleId",
              newValue.map((role: any) => role.id),
            );
          }}
          renderTags={(value, getTagProps) => (
            <div>
              <Chip size="small" variant="outlined" label={value[0].value} {...getTagProps({ index: 0 })} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((val) => val?.value ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </div>
          )}
          getOptionLabel={(role) => role.value}
          multiple
          renderInput={(params) => (
            <TextField
              className="filter-input"
              {...params}
              label="Select Maintainer"
              placeholder="Select Maintainer"
            />
          )}
        />
        <Button variant="contained" onClick={() => onFilter(privateFilter)}>
          Filter
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            onFilter(initialFilters);
            setPrivateFilter(initialFilters);
          }}
        >
          Clear
        </Button>
      </div>
    </>
  );
};

export const DivisionSectionStationFilter = ({ filter, onChangeFilter }: { filter: any; onChangeFilter: any }) => {
  const { outerToInnerKeys: divisionLists, innerToCustomerNames: station, outermostKeys: zone, customerNameToIdObject: customer_name_to_id } = usePermissions();
  // const { divisions, divisionLists, station, customer_name_to_id } = useMemo(() => {
  //   const divisionList = getLocalStorage("permissions", []);
  //   const {
  //     outermostKeys: divisions,
  //     outerToInnerKeys: divisionLists,
  //     innerToCustomerNames: station,
  //     customerNameToIdObject: customer_name_to_id,
  //   }: any = extractUserDataFromPermission(JSON.parse(divisionList));

  //   return {
  //     divisions,
  //     divisionLists,
  //     station,
  //     customer_name_to_id,
  //   };
  // }, []);

  // const filteredStations = useMemo(() => {
  //   return Array.isArray(filter?.section)
  //     ? filter?.section
  //       ?.map((section: any) =>
  //         station[section]?.map((station: any) => ({
  //           value: customer_name_to_id[station],
  //           label: station,
  //         })),
  //       )
  //       .flat()
  //     : station[filter?.section]?.map((station: any) => ({
  //       value: customer_name_to_id[station],
  //       label: station,
  //     })) || [];
  // }, [filter?.section]);

  const filteredStations = useMemo(() => {
    if (!station) return [];
    return (Object.values(station) || []).flat().map((station: any) => ({
      value: customer_name_to_id[station],
      label: station,
    }));
  }, [station]);

  // const filteredSections = useMemo(() => {
  //   return filter?.division?.map((division: any) => divisionLists?.[division] || [])?.flat() || [];
  // }, [filter?.division]);

  return (
    <>
      {/* <Autocomplete
        fullWidth
        size="small"
        className="filter-input auto-complete"
        options={divisions}
        disableCloseOnSelect
        multiple
        renderInput={(params) => (
          <TextField className="filter-input" {...params} label="Select Division" placeholder="Select Division" />
        )}
        value={filter?.division || []}
        onChange={(_, newValue) => {
          onChangeFilter("division", newValue || []);
          onChangeFilter("section", []);
          onChangeFilter("station", []);
        }}
        renderTags={(value, getTagProps) => {
          return (
            <div>
              <Chip size="small" variant="outlined" label={value[0]} {...getTagProps({ index: 0 })} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((val) => val ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </div>
          );
        }}
      />

      <Autocomplete
        fullWidth
        size="small"
        className="filter-input auto-complete"
        disableCloseOnSelect
        multiple
        options={filteredSections}
        renderInput={(params) => (
          <TextField className="filter-input" {...params} label="Select Section" placeholder="Select Section" />
        )}
        value={filter?.section || []}
        onChange={(_, newValue) => {
          onChangeFilter("section", newValue || []);
          onChangeFilter("station", []);
        }}
        renderTags={(value, getTagProps) => {
          return (
            <div>
              <Chip size="small" variant="outlined" label={value[0]} {...getTagProps({ index: 0 })} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((val) => val ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </div>
          );
        }}
      /> */}

      <Autocomplete
        fullWidth
        size="small"
        multiple
        disableCloseOnSelect
        className="filter-input auto-complete"
        options={filteredStations}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={
          filter?.station
            ? filteredStations?.filter((station: any) =>
              filter?.station?.some((value: any) => station.value === value),
            )
            : []
        }
        renderInput={(params) => (
          <TextField {...params} className="filter-input" label="Stations" placeholder="Stations" />
        )}
        onChange={(_, newValue) => {
          onChangeFilter(
            "station",
            newValue.map((station: any) => station.value),
          );
        }}
        renderTags={(value, getTagProps) => {
          return (
            <div>
              <Chip size="small" variant="outlined" label={value[0].label} {...getTagProps({ index: 0 })} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((val) => val?.label ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </div>
          );
        }}
      />
    </>
  );
};
